import React, {useMemo, useState} from "react";
import {useForm, FormProvider} from "react-hook-form";
import PropTypes from "prop-types";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faPlus} from "@fortawesome/free-solid-svg-icons";
import styled, {css} from "styled-components";
import {yupResolver} from "@hookform/resolvers/yup";
import * as yup from "yup";

// Utils
import {toTitleCase} from "../../utils/helpers.js";
import {COMPONENT, FIELD, GROUP} from "../../utils/builder.js";

// Components
import InputText from "../../components/form/InputText.js";

// Style
import {pad} from "../../style/components/variables.js";
import {flex} from "../../style/components/mixins.js";
import {Button, Form} from "../../style/components/general.js";

const AddElement = ({builder, addElement, parent}) => {
  const {allIds, byId} = builder;

  const parentElement = useMemo(() => byId[parent], [byId, parent]);

  const options = useMemo(() => {
    if (parentElement?.element && parentElement.element === COMPONENT) return [FIELD];
    if (parentElement?.element && parentElement.element === GROUP) return [COMPONENT, FIELD];
    return [GROUP, FIELD];
  }, [parentElement]);

  const [current, setCurrent] = useState(0);
  const [element, setElement] = useState(options[0]);

  const placeholder = useMemo(() => {
    if (element === FIELD) return "Add Field...";
    if (element === COMPONENT) return "Add Component...";
    return "Add Group...";
  }, [element]);

  const schema = yup.object().shape({
    label: yup
      .string()
      .required(`Please provide a ${element} name.`)
      .test({
        test: val => !val.match(/.*[.,'"].*/),
        message: `${toTitleCase(element)} name may not contain periods, commas or quotation marks.`
      })
      .test({
        test: val => {
          if (parent)
            return (
              byId[parent]?.children?.filter(
                key =>
                  byId[key] &&
                  byId[key].label?.toLowerCase() === val.toLowerCase() &&
                  !byId[key].deleted
              )?.length === 0
            );

          return (
            allIds.filter(
              key =>
                byId[key] &&
                byId[key]?.label?.toLowerCase() === val.toLowerCase() &&
                !byId[key].deleted
            )?.length === 0
          );
        },
        message: parent
          ? `Component must be unique within ${byId[parent].label}`
          : "Group must be unique within builder"
      })
  });

  const form = useForm({
    defaultValues: {label: ""},
    resolver: yupResolver(schema)
  });
  const {handleSubmit, reset} = form;

  const handleAddElement = ({label}) => {
    addElement({
      element,
      label,
      children: [],
      linkedParent: parent
    });
    reset();
  };

  return (
    <FormProvider {...form}>
      <Form onSubmit={handleSubmit(handleAddElement)} noValidate>
        <FormFieldWrapper data-testid="addField" parent={parentElement?.element}>
          <InputText name="label" testId="label-input" placeholder={placeholder} required />
          {(!parentElement || parentElement?.element !== COMPONENT) && (
            <Target
              type="button"
              title="Toggle Element"
              onClick={() => {
                const targetState = current === 0 ? 1 : 0;
                setElement(options[targetState]);
                setCurrent(targetState);
              }}
              element={element}
              disabled={parentElement?.element === COMPONENT}>
              {element === GROUP && "G"}
              {element === COMPONENT && "C"}
              {element === FIELD && "F"}
            </Target>
          )}
          <Button type="submit">
            <FontAwesomeIcon icon={faPlus} />
          </Button>
        </FormFieldWrapper>
      </Form>
    </FormProvider>
  );
};

AddElement.propTypes = {
  builder: PropTypes.objectOf(PropTypes.any).isRequired,
  addElement: PropTypes.func.isRequired,
  parent: PropTypes.string,
  units: PropTypes.arrayOf(PropTypes.any)
};

const FormFieldWrapper = styled.div`
  ${flex("row", "nowrap", "space-between", "center")};
  width: 100%;

  ${({parent}) => {
    if (parent === GROUP || parent === COMPONENT)
      return css`
        margin: ${pad}px 0;
      `;

    return css`
      margin: 0;
    `;
  }};

  div {
    margin-top: 0;
  }

  ${Button} {
    margin-left: ${pad}px;
  }
`;

const Target = styled(Button)`
  min-width: 35px;

  ${({disabled}) =>
    disabled &&
    css`
      pointer-events: none;
      cursor: not-allowed;
    `}

  ${({element}) =>
    element === GROUP &&
    css`
      background: ${({theme}) => theme.group};
    `};

  ${({element}) =>
    element === COMPONENT &&
    css`
      background: ${({theme}) => theme.component};
    `};

  ${({element}) =>
    element === FIELD &&
    css`
      background: ${({theme}) => theme.field};
    `};
`;

export default AddElement;
